/**
 * Kong is the only browser-facing API gateway (SECURITY.md).
 *
 * - **Production / static build:** set `VITE_API_BASE_URL` to your Kong origin (no trailing slash),
 *   e.g. `https://api.example.com` or `http://127.0.0.1:8000`.
 * - **Vite dev:** leave `VITE_API_BASE_URL` empty so requests use same-origin `/api/...`; set
 *   `VITE_PROXY_API_TARGET` at **build time** to your Kong URL so the dev server forwards `/api` to Kong.
 *
 * There is no alternate gateway env — if traffic does not reach Kong, fix proxy, URL, or Kong routes.
 */

export function kongBrowserOrigin() {
  return String(import.meta.env.VITE_API_BASE_URL || "").trim().replace(/\/$/, "");
}

/**
 * Absolute URL for a path under Kong (path must start with `/api/...`).
 * @param {string} path
 */
export function kongBrowserFetchUrl(path) {
  const p = path.startsWith("/") ? path : `/${path}`;
  const origin = kongBrowserOrigin();
  if (origin) return `${origin}${p}`;
  // No VITE_API_BASE_URL set — use relative paths so the web server's reverse proxy
  // (Nginx /api → Kong) handles routing. Works in both Vite dev mode and Docker/Nginx.
  return p;
}

/** Shown when the gateway returns HTTP 404 for an auth route (request reached the server; path not routed). */
export const KONG_LOGIN_404_HELP =
  "The API gateway returned 404 for this path (the request was accepted; nothing is registered to handle it). " +
  "Check: (1) Kong has a route for /api/v1/auth (or /api) that proxies to auth-service; " +
  "(2) auth-service is mounted at the path Kong forwards to (e.g. strip-prefix vs full path); " +
  "(3) auth-service exposes POST /api/v1/auth/tenant-dashboard/login; " +
  "(4) in dev, VITE_PROXY_API_TARGET is Kong and you restarted Vite after changing .env; " +
  "(5) in prod, VITE_API_BASE_URL is the Kong public URL.";

/** Shown when Kong or the Vite dev proxy returns HTTP 502 for an auth call. */
export const KONG_OR_PROXY_502_HELP =
  "502 Bad Gateway: a hop between your browser and auth-service failed (invalid or empty response, or upstream unreachable). " +
  "If the Vite terminal shows \"socket hang up\", Kong closed the TCP connection before a full HTTP response " +
  "(upstream auth unreachable, Kong/Lua error, or timeout). " +
  "Check: (1) auth-service is running and matches Kong auth-service upstream URL/port (see startup log HTTP listen); " +
  "(2) from Kong host/container, curl auth /health/live; " +
  "(3) Docker: host.docker.internal often needs extra_hosts; Linux may use host LAN IP; " +
  "(4) npm run dev terminal for [vite /api proxy] lines; " +
  "(5) Kong and auth-service error logs.";
